import { ChevronRight, ExternalLink } from 'lucide-react';
import { Reveal } from '../hooks/useReveal.tsx';
import { MeshText } from './MeshText';

export function AboutSection() {
  const capabilities = [
    {
      number: '01',
      title: 'Full Stack Development',
      description: 'Building complete web applications from frontend to backend with modern frameworks and scalable architectures.',
    },
    {
      number: '02',
      title: 'AI & Machine Learning',
      description: 'Exploring neural networks, data analysis, and intelligent systems to create smarter applications.',
    },
    {
      number: '03',
      title: 'Continuous Learning',
      description: 'Self-taught developer constantly expanding skills through projects, courses, and open-source contributions.',
    },
  ];

  return (
    <section
      id="about"
      className="flex flex-col justify-between px-5 sm:px-8 md:px-12 py-16 sm:py-20"
    >
      {/* Top Row */}
      <div className="flex flex-col gap-8 sm:flex-row sm:justify-between">
        {/* Badge */}
        <Reveal delay={120}>
          <div className="accent-badge px-3 py-1.5 rounded-r-md inline-block">
            <span className="mono-label-sm text-white/90">
              About Me
            </span>
          </div>
        </Reveal>

        {/* Right Copy */}
        <Reveal delay={220} className="max-w-sm sm:text-right">
          <p className="text-lg sm:text-xl leading-relaxed text-white text-shadow">
            A passionate developer who turns ideas into reality through clean code and creative problem-solving.
          </p>
        </Reveal>
      </div>

      {/* Bottom Area */}
      <div className="flex flex-col mt-10">
        <div className="flex flex-col gap-12 md:flex-row md:items-end md:justify-between md:gap-16">
          {/* Left Column - Headline and CTAs */}
          <div className="max-w-xl">
            {/* Headline */}
            <Reveal delay={180}>
              <div className="flex flex-col gap-0">
                <div className="h-[60px] sm:h-[75px] lg:h-[90px] w-[280px] sm:w-[350px] lg:w-[420px]">
                  <MeshText
                    text="Learn. Build."
                    color="#ffffff"
                    fontFamily="Inter"
                    fontWeight={500}
                    fontSize={65}
                    colorSplit={true}
                    splitColors={['#61dafb', '#ff40c0']}
                    force={18}
                  />
                </div>
                <div className="h-[60px] sm:h-[75px] lg:h-[90px] w-[180px] sm:w-[220px] lg:w-[260px]">
                  <MeshText
                    text="Grow."
                    color="#ffffff"
                    fontFamily="Inter"
                    fontWeight={500}
                    fontSize={65}
                    colorSplit={true}
                    splitColors={['#40ff80', '#61dafb']}
                    force={18}
                  />
                </div>
              </div>
            </Reveal>

            {/* Body Text */}
            <Reveal delay={320}>
              <p className="mt-6 max-w-md text-sm sm:text-base text-white/80 text-shadow">
                Software Engineering student at Delhi Technological University. 
                Started coding in high school, now building full-stack applications and 
                exploring the frontiers of AI and machine learning.
              </p>
            </Reveal>

            {/* CTAs */}
            <Reveal delay={420}>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#projects"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-primary rounded-full px-5 py-2.5 text-xs sm:text-sm font-medium inline-flex items-center gap-1"
                >
                  View Projects
                  <ChevronRight size={14} />
                </a>
                <a
                  href="https://github.com/Paras1209"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary rounded-full px-5 py-2.5 text-xs sm:text-sm font-medium inline-flex items-center gap-1"
                >
                  GitHub Profile
                  <ExternalLink size={14} />
                </a>
              </div>
            </Reveal>
          </div>

          {/* Right - Capability Panel */}
          <div className="w-full max-w-md rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md">
            {capabilities.map((cap, index) => (
              <Reveal key={cap.number} delay={300 + index * 110}>
                <div
                  className={`capability-row flex gap-5 px-5 sm:px-6 py-5 cursor-default ${
                    index < capabilities.length - 1 ? 'border-b border-white/15' : ''
                  }`}
                >
                  {/* Number */}
                  <span className="mono-label text-white/55 pt-1 flex-shrink-0">
                    {cap.number}
                  </span>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="h-[24px] sm:h-[28px] w-[220px] sm:w-[260px]">
                        <MeshText
                          text={cap.title}
                          color="#ffffff"
                          fontFamily="Inter"
                          fontWeight={500}
                          fontSize={20}
                          colorSplit={true}
                          splitColors={['#61dafb', '#40ff80']}
                          force={14}
                        />
                      </div>
                      <ChevronRight
                        size={16}
                        className="capability-chevron text-white/40"
                      />
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-white/70">
                      {cap.description}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
